import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import ConfirmDialog from '../components/ui/ConfirmDialog';

/**
 * Promise-based replacement for window.confirm.
 *
 * window.confirm blocks the whole event loop, cannot be styled or tested, and
 * in this app it guarded deletes for campaigns, profiles, tags, API keys and
 * licences. This keeps the call shape those sites already used:
 *
 *   if (!await confirm('Delete this campaign?')) return;
 *
 * so a migration is a one-line change per site, with the enclosing handler
 * marked async.
 */

const ConfirmContext = createContext(null);

const normalize = (input) => {
    if (typeof input === 'string') return { title: input };
    return input || {};
};

export const ConfirmProvider = ({ children }) => {
    const [request, setRequest] = useState(null);
    // The resolver is kept in a ref so resolving does not depend on the
    // render that created it.
    const resolverRef = useRef(null);

    const settle = useCallback((result) => {
        const resolve = resolverRef.current;
        resolverRef.current = null;
        setRequest(null);
        if (resolve) resolve(result);
    }, []);

    const confirm = useCallback((input) => {
        const options = normalize(input);
        return new Promise((resolve) => {
            // A second call while one is open would strand the first caller's
            // promise, so the earlier one is answered "no" before replacing it.
            if (resolverRef.current) resolverRef.current(false);
            resolverRef.current = resolve;
            setRequest(options);
        });
    }, []);

    const value = useMemo(() => ({ confirm }), [confirm]);

    return (
        <ConfirmContext.Provider value={value}>
            {children}
            <ConfirmDialog
                open={Boolean(request)}
                title={request?.title || 'Are you sure?'}
                message={request?.message}
                confirmLabel={request?.confirmLabel}
                cancelLabel={request?.cancelLabel}
                destructive={Boolean(request?.destructive)}
                onConfirm={() => settle(true)}
                onCancel={() => settle(false)}
            />
        </ConfirmContext.Provider>
    );
};

export const useConfirm = () => {
    const context = useContext(ConfirmContext);
    if (!context) {
        throw new Error('useConfirm must be used within a ConfirmProvider');
    }
    return context.confirm;
};

export default ConfirmContext;
